import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { AppHeader } from './AppHeader';

export interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: string;
  disabled?: boolean;
  comingSoon?: boolean;
  /** Minimum admin role needed to see this item. 'marketer' items are visible to all admin roles. Defaults to 'super_admin'. */
  minRole?: 'super_admin' | 'marketer';
}

export interface BaseLayoutProps {
  children: React.ReactNode;
  navItems: NavItem[];
  sidebarTitle: string;
  sidebarDescription: string;
  headerSubtitle: string;
  rootPath: string;
}

export function BaseLayout({
  children,
  navItems,
  sidebarTitle,
  sidebarDescription,
  headerSubtitle,
  rootPath
}: BaseLayoutProps) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Close mobile menu on navigation
  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Determine the single active nav item by choosing the longest matching href prefix or exact match
  const activeHref = React.useMemo(() => {
    const matches = navItems.filter((item) => {
      if (item.href === rootPath) return location.pathname === rootPath;
      return location.pathname === item.href || location.pathname.startsWith(item.href + '/');
    });
    if (matches.length === 0) return undefined as string | undefined;
    return matches.reduce((prev, cur) => (cur.href.length > prev.href.length ? cur : prev)).href;
  }, [location.pathname, navItems, rootPath]);

  const sidebarContent = (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">{sidebarTitle}</h2>
        <p className="text-sm text-muted-foreground">{sidebarDescription}</p>
      </div>

      <div className="space-y-2">
        {navItems.map((item) => {
          const isActive = item.href === activeHref;
          const isDisabled = item.comingSoon;

          if (isDisabled) {
            return (
              <div
                key={item.href}
                className="flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg text-gray-400 cursor-not-allowed"
              >
                <span className="opacity-50">{item.icon}</span>
                <span className="flex-1">{item.label}</span>
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">
                  Coming Soon
                </span>
              </div>
            );
          }

          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-all',
                isActive
                  ? 'bg-primary-50 text-primary-700 border border-primary-200 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              )}
            >
              {item.icon}
              <span className="flex-1">{item.label}</span>
              {item.badge && (
                <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <AppHeader
        onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
        mobileMenuOpen={mobileMenuOpen}
      />

      <div className="flex relative">
        {/* Mobile overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/30 z-30 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar Navigation - desktop: static, mobile: slide-out drawer */}
        <nav
          className={cn(
            'bg-white border-r border-gray-200 min-h-[calc(100vh-3rem)] overflow-y-auto',
            // Desktop: always visible, static
            'hidden md:block md:w-72 md:sticky',
            // Mobile: fixed drawer
            mobileMenuOpen && 'fixed top-12 left-0 bottom-0 w-72 z-40 block shadow-xl'
          )}
          style={{ top: 'calc(3rem + var(--verification-banner-h, 0px))' }}
        >
          {sidebarContent}
        </nav>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <div className="h-full">
            <div className="max-w-7xl mx-auto">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
